import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// 异步actions
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchDashboardData',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/dashboard', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取看板数据失败');
    }
  }
);

export const fetchRealtimeStats = createAsyncThunk(
  'dashboard/fetchRealtimeStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/dashboard/realtime');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取实时数据失败');
    }
  }
);

export const fetchUserStats = createAsyncThunk(
  'dashboard/fetchUserStats',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/dashboard/users', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取用户统计失败');
    }
  }
);

export const fetchFinancialStats = createAsyncThunk(
  'dashboard/fetchFinancialStats',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/dashboard/financial', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取财务统计失败');
    }
  }
);

export const fetchOrderStats = createAsyncThunk(
  'dashboard/fetchOrderStats',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/dashboard/orders', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取订单统计失败');
    }
  }
);

// 初始状态
const initialState = {
  realtimeStats: {
    onlineUsers: 0,
    todayOrders: 0,
    todayRecharge: 0,
    todayWithdraw: 0,
  },
  userStats: {
    totalUsers: 0,
    todayNewUsers: 0,
    activeUsers: 0,
    yesterdayNewUsers: 0,
  },
  financialStats: {
    totalBalance: 0,
    frozenBalance: 0,
    availableBalance: 0,
    totalRecharge: 0,
    totalWithdraw: 0,
  },
  orderStats: {
    totalOrders: 0,
    successOrders: 0,
    pendingOrders: 0,
    failedOrders: 0,
    successRate: 0,
  },
  charts: {
    userGrowth: [],
    financialFlow: [],
    orderTrends: [],
    productDistribution: [],
    thirdPartyChart: [],
  },
  thirdPartyStats: {
    todayPayAmount: '0',
    todayPayCount: 0,
    pendingPayCount: 0,
    totalPayAmount: '0',
    totalPayCount: 0,
    todayWithdrawAmount: '0',
    todayWithdrawCount: 0,
    pendingWithdrawCount: 0,
    todayWaitWithdrawAmount: '0',
    totalWithdrawAmount: '0',
    totalWithdrawCount: 0,
    merchantTotalBalance: '0',
    totalWaitSettleAmount: '0',
    totalMerchantRecharge: '0',
    totalMerchantWithdraw: '0',
  },
  isLoading: false,
  error: null,
};

// slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateRealtimeStats: (state, action) => {
      state.realtimeStats = { ...state.realtimeStats, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取看板数据
      .addCase(fetchDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        const { realtimeStats, userStats, financialStats, orderStats, charts, thirdPartyStats } = action.payload;
        state.realtimeStats = realtimeStats;
        state.userStats = userStats;
        state.financialStats = financialStats;
        state.orderStats = orderStats;
        state.charts = charts;
        state.thirdPartyStats = thirdPartyStats;
        state.error = null;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 获取实时统计
      .addCase(fetchRealtimeStats.fulfilled, (state, action) => {
        state.realtimeStats = action.payload;
      })
      // 获取用户统计
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.userStats = action.payload;
      })
      // 获取财务统计
      .addCase(fetchFinancialStats.fulfilled, (state, action) => {
        state.financialStats = action.payload;
      })
      // 获取订单统计
      .addCase(fetchOrderStats.fulfilled, (state, action) => {
        state.orderStats = action.payload;
      });
  },
});

export const { clearError, updateRealtimeStats } = dashboardSlice.actions;
export default dashboardSlice.reducer;
